import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, WifiOff, Info, ChevronRight, ShieldAlert, Search, TruckIcon, Phone, Navigation, PackageIcon, CheckCircle2, XCircle, MoreVertical } from 'lucide-react';
import { Alert, Severity, mockDrivers, mockDeliveries } from '../data/mockData';
interface AlertFeedProps {
  alerts: Alert[];
  selectedAlertId: string | null;
  onSelectAlert: (alertId: string, driverId: string) => void;
  requestedSeverityFilter?: {
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null;
  onContactDriver?: (driverId: string) => void;
  onHoverAlert?: (driverIds: string[] | null) => void;
  resolvedAlertIds?: string[];
  dismissedAlertIds?: string[];
  onResolveAlert?: (alertId: string) => void;
  onDismissAlert?: (alertId: string) => void;
}
export function AlertFeed({
  alerts,
  selectedAlertId,
  onSelectAlert,
  requestedSeverityFilter,
  onContactDriver,
  onHoverAlert,
  resolvedAlertIds = [],
  dismissedAlertIds = [],
  onResolveAlert,
  onDismissAlert
}: AlertFeedProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'hos' | 'routing' | 'signal'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning' | 'low'>('all');
  const [filterMode, setFilterMode] = useState<'none' | 'type' | 'severity'>('severity');
  const [openDropdownAlertId, setOpenDropdownAlertId] = useState<string | null>(null);
  const [confirmDismissAlertId, setConfirmDismissAlertId] = useState<string | null>(null);
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownAlertId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  // Compute filter counts
  const typeCounts = {
    all: alerts.length,
    hos: alerts.filter(a => a.type === 'hos').length,
    routing: alerts.filter(a => a.type === 'bridge' || a.type === 'traffic').length,
    signal: alerts.filter(a => a.type === 'offline').length
  };
  const severityCounts = {
    all: alerts.length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    low: alerts.filter(a => a.severity === 'info' || a.severity === 'offline').length
  };
  useEffect(() => {
    if (requestedSeverityFilter) {
      setSeverityFilter(requestedSeverityFilter.severity);
    }
  }, [requestedSeverityFilter]);
  const severityOrder: Record<Severity, number> = {
    critical: 0,
    warning: 1,
    offline: 2,
    info: 3,
    normal: 4
  };
  const activeAlerts = alerts.filter(a => !resolvedAlertIds.includes(a.id) && !dismissedAlertIds.includes(a.id));
  const sortedAlerts = [...activeAlerts].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  const isVisible = (a: Alert) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!a.title.toLowerCase().includes(q) && !a.description.toLowerCase().includes(q)) return false;
    }
    if (typeFilter === 'hos' && a.type !== 'hos') return false;
    if (typeFilter === 'routing' && a.type !== 'bridge' && a.type !== 'traffic') return false;
    if (typeFilter === 'signal' && a.type !== 'offline') return false;
    if (severityFilter === 'critical' && a.severity !== 'critical') return false;
    if (severityFilter === 'warning' && a.severity !== 'warning') return false;
    if (severityFilter === 'low' && a.severity !== 'info' && a.severity !== 'offline') return false;
    return true;
  };
  const visibleCount = sortedAlerts.filter(isVisible).length;
  const resolvedAlerts = alerts.filter(a => resolvedAlertIds.includes(a.id));
  const dismissedAlerts = alerts.filter(a => dismissedAlertIds.includes(a.id));
  return <>
      <div className="p-3 border-b border-slate-800 space-y-3 shrink-0" data-id="element-11">
        {/* Filter Mode Tabs */}
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-12">
          <button onClick={() => {
          if (filterMode === 'severity') {
            setFilterMode('none');
          } else {
            setFilterMode('severity');
            setTypeFilter('all');
          }
        }} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium ${filterMode === 'severity' ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-13">
            Severity
          </button>
          <button onClick={() => {
          if (filterMode === 'type') {
            setFilterMode('none');
          } else {
            setFilterMode('type');
            setSeverityFilter('all');
          }
        }} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium ${filterMode === 'type' ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-14">
            Type
          </button>
        </div>

        {/* Severity Filter Options */}
        {filterMode === 'severity' && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-15">
            <FilterTab active={severityFilter === 'all'} onClick={() => setSeverityFilter('all')} label="All" count={severityCounts.all} data-id="element-16" />
            <FilterTab active={severityFilter === 'critical'} onClick={() => setSeverityFilter('critical')} label="Critical" count={severityCounts.critical} data-id="element-17" />
            <FilterTab active={severityFilter === 'warning'} onClick={() => setSeverityFilter('warning')} label="Warn" count={severityCounts.warning} data-id="element-18" />
            <FilterTab active={severityFilter === 'low'} onClick={() => setSeverityFilter('low')} label="Low" count={severityCounts.low} data-id="element-19" />
          </div>}

        {/* Type Filter Options */}
        {filterMode === 'type' && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-20">
            <FilterTab active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} label="All" count={typeCounts.all} data-id="element-21" />
            <FilterTab active={typeFilter === 'hos'} onClick={() => setTypeFilter('hos')} label="HOS" count={typeCounts.hos} data-id="element-22" />
            <FilterTab active={typeFilter === 'routing'} onClick={() => setTypeFilter('routing')} label="Routing" count={typeCounts.routing} data-id="element-23" />
            <FilterTab active={typeFilter === 'signal'} onClick={() => setTypeFilter('signal')} label="Signal" count={typeCounts.signal} data-id="element-24" />
          </div>}

        {/* Search */}
        <div className="relative" data-id="element-25">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-26" />
          <input type="text" placeholder="Search alerts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-slate-600 transition-colors" data-id="element-27" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-28">
        {sortedAlerts.map(alert => <AlertCard key={alert.id} alert={alert} isSelected={selectedAlertId === alert.id} onClick={() => onSelectAlert(alert.id, alert.driverId)} hidden={!isVisible(alert)} onContactDriver={onContactDriver} onHoverAlert={onHoverAlert} onResolveAlert={onResolveAlert} onDismissAlert={onDismissAlert} openDropdownAlertId={openDropdownAlertId} setOpenDropdownAlertId={setOpenDropdownAlertId} confirmDismissAlertId={confirmDismissAlertId} setConfirmDismissAlertId={setConfirmDismissAlertId} data-id="element-29" />)}

        {visibleCount === 0 && <div className="text-center p-8 text-slate-500 text-sm" data-id="element-30">
            <ShieldAlert className="w-8 h-8 mx-auto mb-3 opacity-20" data-id="element-31" />
            No active alerts match your filters.
          </div>}

        {resolvedAlerts.length > 0 && <div className="mt-6 mb-2" data-id="element-32">
            <div className="flex items-center gap-2 mb-3 px-1" data-id="element-33">
              <CheckCircle2 className="w-4 h-4 text-brand-green" data-id="element-34" />
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-35">
                Resolved
              </h3>
            </div>
            {resolvedAlerts.map(alert => <div key={alert.id} className="opacity-60 grayscale" data-id="element-36">
                <AlertCard alert={alert} isSelected={false} onClick={() => {}} hidden={false} data-id="element-37" />
              </div>)}
          </div>}

        {dismissedAlerts.length > 0 && <div className="mt-6 mb-2" data-id="element-38">
            <div className="flex items-center gap-2 mb-3 px-1" data-id="element-39">
              <XCircle className="w-4 h-4 text-slate-500" data-id="element-40" />
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-41">
                Dismissed
              </h3>
            </div>
            {dismissedAlerts.map(alert => <div key={alert.id} className="opacity-40 grayscale" data-id="element-42">
                <AlertCard alert={alert} isSelected={false} onClick={() => {}} hidden={false} data-id="element-43" />
              </div>)}
          </div>}
      </div>
    </>;
}
function FilterTab({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return <button onClick={onClick} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium flex items-center justify-center gap-1.5
        ${active ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-44">
      {label}
      {count !== undefined && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-45">
          {count}
        </span>}
    </button>;
}
function AlertCard({
  alert,
  isSelected,
  onClick,
  hidden,
  onContactDriver,
  onHoverAlert,
  onResolveAlert,
  onDismissAlert,
  openDropdownAlertId,
  setOpenDropdownAlertId,
  confirmDismissAlertId,
  setConfirmDismissAlertId
}: {
  alert: Alert;
  isSelected: boolean;
  onClick: () => void;
  hidden: boolean;
  onContactDriver?: (driverId: string) => void;
  onHoverAlert?: (driverIds: string[] | null) => void;
  onResolveAlert?: (alertId: string) => void;
  onDismissAlert?: (alertId: string) => void;
  openDropdownAlertId?: string | null;
  setOpenDropdownAlertId?: (id: string | null) => void;
  confirmDismissAlertId?: string | null;
  setConfirmDismissAlertId?: (id: string | null) => void;
}) {
  const noop = () => {};
  const setDropdown = setOpenDropdownAlertId ?? noop;
  const setConfirmDismiss = setConfirmDismissAlertId ?? noop;
  const config = {
    critical: {
      icon: AlertTriangle,
      color: 'text-brand-red',
      bg: 'bg-brand-red/10',
      border: 'border-brand-red/30',
      pulse: true
    },
    warning: {
      icon: AlertTriangle,
      color: 'text-brand-amber',
      bg: 'bg-brand-amber/10',
      border: 'border-brand-amber/30',
      pulse: false
    },
    offline: {
      icon: WifiOff,
      color: 'text-slate-400',
      bg: 'bg-slate-800/50',
      border: 'border-slate-700',
      pulse: false
    },
    info: {
      icon: Info,
      color: 'text-brand-blue',
      bg: 'bg-brand-blue/10',
      border: 'border-brand-blue/30',
      pulse: false
    },
    normal: {
      icon: Info,
      color: 'text-slate-400',
      bg: 'bg-slate-800',
      border: 'border-slate-700',
      pulse: false
    }
  }[alert.severity];
  const Icon = config.icon;
  if (hidden) return null;
  const handleMouseEnter = () => {
    if (!onHoverAlert) return;
    const ids = alert.affectedDriverIds && alert.affectedDriverIds.length > 0 ? alert.affectedDriverIds : [alert.driverId];
    onHoverAlert(ids);
  };
  const handleMouseLeave = () => {
    onHoverAlert?.(null);
  };
  return <div onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`relative p-3 mb-3 last:mb-0 rounded-xl border cursor-pointer transition-all
        ${isSelected ? `${config.bg} ${config.border} shadow-[0_0_15px_rgba(0,0,0,0.2)]` : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}
      `} data-id="element-46">
      {isSelected && <motion.div layoutId="activeAlertIndicator" className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${config.color.replace('text-', 'bg-')}`} data-id="element-47" />}

      <div className="flex items-start gap-3" data-id="element-48">
        <div className={`mt-0.5 relative ${config.color}`} data-id="element-49">
          <Icon className="w-5 h-5" data-id="element-50" />
        </div>

        <div className="flex-1 min-w-0" data-id="element-51">
          <div className="flex items-center justify-between mb-1" data-id="element-52">
            <h3 className={`text-sm font-semibold truncate ${isSelected ? 'text-slate-100' : 'text-slate-300'}`} data-id="element-53">
              {alert.title}
            </h3>
            <span className={`text-[11px] font-semibold font-mono shrink-0 ml-2 ${alert.severity === 'critical' || alert.severity === 'warning' ? 'text-white' : 'text-slate-500'}`} data-id="element-54">
              {alert.timeContext}
            </span>
          </div>

          {alert.affectedDriverIds ? (() => {
          const atRiskDrivers = mockDrivers.filter(d => alert.affectedDriverIds!.includes(d.id) && (d.status === 'warning' || d.status === 'critical')).length;
          const atRiskDeliveries = mockDeliveries.filter(d => alert.affectedDriverIds!.includes(d.driverId) && d.status === 'at-risk').length;
          const totalDeliveries = mockDeliveries.filter(d => alert.affectedDriverIds!.includes(d.driverId) && d.status !== 'completed').length;
          return <div className="mb-1.5 space-y-0.5" data-id="element-55">
                    <div className="flex items-center gap-1.5" data-id="element-56">
                      <TruckIcon className="w-3 h-3 text-slate-400" data-id="element-57" />
                      <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-58">
                        <span className="text-slate-400" data-id="element-59">
                          {alert.affectedDriverIds.length} trucks affected
                        </span>
                        {atRiskDrivers > 0 && <>
                            <span className="text-slate-400" data-id="element-60"> / </span>
                            <span className="text-brand-amber" data-id="element-61">
                              {atRiskDrivers} at risk
                            </span>
                          </>}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-62">
                      <PackageIcon className="w-3 h-3 text-slate-400" data-id="element-63" />
                      <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-64">
                        <span className="text-slate-400" data-id="element-65">
                          {totalDeliveries} deliveries affected
                        </span>
                        {atRiskDeliveries > 0 && <>
                            <span className="text-slate-400" data-id="element-66"> / </span>
                            <span className="text-brand-amber" data-id="element-67">
                              {atRiskDeliveries} at risk
                            </span>
                          </>}
                      </span>
                    </div>
                  </div>;
        })() : (() => {
          const driver = mockDrivers.find(d => d.id === alert.driverId);
          return driver ? <div className="flex items-center gap-1.5 mb-1.5 text-slate-400" data-id="element-68">
                    <TruckIcon className="w-3 h-3" data-id="element-69" />
                    <span className="text-[10px] font-bold uppercase tracking-wider font-mono" data-id="element-70">
                      {driver.truckId}
                    </span>
                  </div> : null;
        })()}

          <p className="text-xs text-slate-400 line-clamp-2 mb-2 leading-relaxed" data-id="element-71">
            {alert.description}
          </p>

          {(alert.severity === 'critical' || alert.severity === 'warning') && <>
              {confirmDismissAlertId === alert.id ? <div className="flex items-center justify-between bg-slate-800/80 rounded-md p-2 border border-slate-700 mt-2" data-id="element-72">
                  <span className="text-[11px] text-slate-300 font-medium" data-id="element-73">
                    <strong className="inline-block mr-1 font-medium" data-id="element-74">
                      Dismiss Alert?
                    </strong>
                  </span>
                  <div className="flex gap-1.5" data-id="element-75">
                    <button onClick={e => {
                e.stopPropagation();
                onDismissAlert?.(alert.id);
                setConfirmDismiss(null);
              }} className="px-2.5 py-1 rounded bg-brand-red hover:bg-brand-red/90 text-white text-[10px] font-semibold transition-colors" data-id="element-76">
                      Yes
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                setConfirmDismiss(null);
              }} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-200 text-[10px] font-semibold transition-colors" data-id="element-77">
                      No
                    </button>
                  </div>
                </div> : <>
                  {alert.actions && alert.actions.length > 0 ? <div className="flex gap-2 mt-2" data-id="element-78">
                      {alert.actions.map((action, idx) => <button key={idx} className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => {
                e.stopPropagation();
                if (action.icon === 'phone') {
                  onContactDriver?.(alert.driverId);
                }
              }} data-id="element-79">
                          {action.icon === 'phone' && <Phone className="w-3 h-3" data-id="element-80" />}
                          {action.icon === 'route' && <Navigation className="w-3 h-3" data-id="element-81" />}
                          {action.label}
                        </button>)}
                      <div className="relative" data-id="element-82">
                        <button onClick={e => {
                  e.stopPropagation();
                  setDropdown(openDropdownAlertId === alert.id ? null : alert.id);
                }} className="w-[28px] h-[28px] flex items-center justify-center rounded-md hover:bg-white/10 text-slate-200 transition-colors bg-[#33415300] border border-white/50" data-id="element-83">
                          <MoreVertical className="w-4 h-4" data-id="element-84" />
                        </button>
                        {openDropdownAlertId === alert.id && <div className="absolute right-0 top-full mt-1 w-36 bg-slate-800 border border-slate-700 rounded-md shadow-xl z-50 overflow-hidden" data-id="element-85">
                            <button onClick={e => {
                    e.stopPropagation();
                    onResolveAlert?.(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-slate-200 hover:bg-slate-700 transition-colors" data-id="element-86">
                              Log as Resolved
                            </button>
                            <button onClick={e => {
                    e.stopPropagation();
                    setConfirmDismiss(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-brand-red hover:bg-slate-700 transition-colors" data-id="element-87">
                              Dismiss
                            </button>
                          </div>}
                      </div>
                    </div> : alert.actionHint && alert.actionHint.toLowerCase() !== 'no action needed' ? alert.affectedDriverIds ? <div className="flex gap-2 mt-2" data-id="element-88">
                        <button className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => e.stopPropagation()} data-id="element-89">
                          <Navigation className="w-3 h-3" data-id="element-90" />
                          Auto Reroute All
                        </button>
                        <div className="relative" data-id="element-91">
                          <button onClick={e => {
                  e.stopPropagation();
                  setDropdown(openDropdownAlertId === alert.id ? null : alert.id);
                }} className="w-[28px] h-[28px] flex items-center justify-center rounded-md hover:bg-white/10 text-slate-200 transition-colors bg-[#33415300] border border-white/50" data-id="element-92">
                            <MoreVertical className="w-4 h-4" data-id="element-93" />
                          </button>
                          {openDropdownAlertId === alert.id && <div className="absolute right-0 top-full mt-1 w-36 bg-slate-800 border border-slate-700 rounded-md shadow-xl z-50 overflow-hidden" data-id="element-94">
                              <button onClick={e => {
                    e.stopPropagation();
                    onResolveAlert?.(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-slate-200 hover:bg-slate-700 transition-colors" data-id="element-95">
                                Log as Resolved
                              </button>
                              <button onClick={e => {
                    e.stopPropagation();
                    setConfirmDismiss(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-brand-red hover:bg-slate-700 transition-colors" data-id="element-96">
                                Dismiss
                              </button>
                            </div>}
                        </div>
                      </div> : <div className="flex gap-2 mt-2" data-id="element-97">
                        <button className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => {
                e.stopPropagation();
                onContactDriver?.(alert.driverId);
              }} data-id="element-98">
                          <Phone className="w-3 h-3" data-id="element-99" />
                          Contact
                        </button>
                        <button className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-[10px] font-semibold transition-colors ${alert.severity === 'critical' ? 'bg-brand-red hover:bg-brand-red/90 text-white' : 'bg-brand-amber hover:bg-brand-amber/90 text-slate-900'}`} onClick={e => e.stopPropagation()} data-id="element-100">
                          <Navigation className="w-3 h-3" data-id="element-101" />
                          Reroute
                        </button>
                        <div className="relative" data-id="element-102">
                          <button onClick={e => {
                  e.stopPropagation();
                  setDropdown(openDropdownAlertId === alert.id ? null : alert.id);
                }} className="w-[28px] h-[28px] flex items-center justify-center rounded-md hover:bg-white/10 text-slate-200 transition-colors bg-[#33415300] border border-white/50" data-id="element-103">
                            <MoreVertical className="w-4 h-4" data-id="element-104" />
                          </button>
                          {openDropdownAlertId === alert.id && <div className="absolute right-0 top-full mt-1 w-36 bg-slate-800 border border-slate-700 rounded-md shadow-xl z-50 overflow-hidden" data-id="element-105">
                              <button onClick={e => {
                    e.stopPropagation();
                    onResolveAlert?.(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-slate-200 hover:bg-slate-700 transition-colors" data-id="element-106">
                                Log as Resolved
                              </button>
                              <button onClick={e => {
                    e.stopPropagation();
                    setConfirmDismiss(alert.id);
                    setDropdown(null);
                  }} className="w-full text-left px-3 py-2 text-[11px] text-brand-red hover:bg-slate-700 transition-colors" data-id="element-107">
                                Dismiss
                              </button>
                            </div>}
                        </div>
                      </div> : null}
                </>}
            </>}
        </div>
      </div>
    </div>;
}